import { FC, useMemo } from 'react'
import { useGetAdsLibraryPageSummaryQuery } from 'src/reactQuery/useGetAdsLibraryPageSummaryQuery'
import { AdLibraryAd } from 'src/reactQuery/useGetAdsLibrarySearchQuery'
import { ReactComponent as CtaButtonClickIcon } from './ctabuttonclick.svg'
import { ReactComponent as ImpressionsIcon } from './eye.svg'
import { ReactComponent as AmountSpentIcon } from './moneys.svg'
import { ReactComponent as AudienceSizeIcon } from './people.svg'
import { ReactComponent as TotalAdsIcon } from './totalads.svg'
import classes from './statsCards.module.scss'

interface StatsCardsProps {
  ads?: AdLibraryAd[]
  isPolitical?: boolean
  pageId: string
}

const PLATFORM_COLORS: Record<string, string> = {
  audience_network: '#FF6600',
  facebook: '#1877F2',
  instagram: '#E1306C',
  messenger: '#0084FF',
  threads: '#000000',
  whatsapp: '#25D366',
}

const prettyPlatform = (name: string) =>
  name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ')

const formatTime = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString()
}

export const StatsCards: FC<StatsCardsProps> = ({
  ads = [],
  isPolitical = false,
  pageId,
}) => {
  const { data: summary, isLoading } = useGetAdsLibraryPageSummaryQuery(pageId)
  const snapshot = summary?.snapshot ?? null
  const status = summary?.snapshot_status ?? null
  const notMonitored = status === 'not_monitored'
  const snapshotFailed = status === 'failed'
  // Syncing = the snapshot is genuinely in flight (request loading or worker
  // pending). NOT not_monitored (gets its own CTA) and NOT a stuck-skeleton
  // for any other null-snapshot case — only pending/loading polls to a result.
  const syncing = !notMonitored && (isLoading || status === 'pending')
  const snapshotTime = formatTime(summary?.last_snapshot_at)

  const platforms = useMemo(() => {
    if (!snapshot) return []
    return Object.entries(snapshot.platforms || {}).map(([name, count]) => ({
      color: PLATFORM_COLORS[name] || '#8080FF',
      count,
      name: prettyPlatform(name),
    }))
  }, [snapshot])

  // CTA buttons stay derived from the currently-loaded ads (creative link
  // titles aren't included in the snapshot).
  const ctaButtons = useMemo(() => {
    const counts: Record<string, number> = {}
    ads.forEach((ad) => {
      ;(ad.ad_creative_link_titles || []).forEach((title) => {
        if (title) counts[title] = (counts[title] || 0) + 1
      })
    })
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([label, count]) => ({ count, label }))
  }, [ads])

  return (
    <>
      <div className={classes.statusRow}>
        {notMonitored ? (
          <span className={classes.statusCta}>
            Save this advertiser to track analytics
          </span>
        ) : syncing ? (
          <span className={classes.statusSyncing}>Syncing…</span>
        ) : (
          <>
            {snapshotFailed && (
              <span
                className={classes.statusWarning}
                title="Last refresh failed — showing the most recent successful snapshot."
              >
                ⚠ Snapshot stale
              </span>
            )}
            {snapshotTime && (
              <span className={classes.statusUpdated}>
                Updated · {snapshotTime}
              </span>
            )}
          </>
        )}
      </div>

      <div className={classes.grid}>
        {/* Total Ads */}
        <div className={classes.card}>
          <div className={classes.cardTop}>
            <div className={classes.cardTitleRow}>
              <TotalAdsIcon />
              <span className={classes.cardTitle}>Total Ads</span>
            </div>
            <span className={classes.cardMainValue}>
              {notMonitored || syncing ? '—' : snapshot?.total_count ?? 0}
            </span>
          </div>
          <div className={classes.cardBottom}>
            {notMonitored ? (
              <span className={classes.noData}>Not tracked yet</span>
            ) : syncing || !snapshot ? (
              <span className={classes.noData}>
                {syncing ? 'Syncing…' : 'No Data'}
              </span>
            ) : (
              <>
                <div className={classes.statItem}>
                  <span className={classes.statValueActive}>
                    {snapshot.active_count}
                  </span>
                  <div className={classes.statLabelRow}>
                    <span
                      className={classes.statDot}
                      style={{ background: '#8080FF' }}
                    />
                    <span className={classes.statLabel}>Active Ads</span>
                  </div>
                </div>
                <div className={classes.statDivider} />
                <div className={classes.statItem}>
                  <span className={classes.statValue}>
                    {snapshot.inactive_count}
                  </span>
                  <div className={classes.statLabelRow}>
                    <span
                      className={classes.statDot}
                      style={{ background: '#FF6B35' }}
                    />
                    <span className={classes.statLabel}>Inactive Ads</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Platforms */}
        <div className={classes.card}>
          <div className={classes.cardTop}>
            <div className={classes.cardTitleRow}>
              <svg
                fill="none"
                height="16"
                viewBox="0 0 24 24"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  opacity="0.6"
                  r="10"
                  stroke="#25262D"
                  strokeWidth="1.5"
                />
                <path
                  d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
                  opacity="0.6"
                  stroke="#25262D"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
              </svg>
              <span className={classes.cardTitle}>Platforms</span>
            </div>
          </div>
          <div className={classes.platformsGrid}>
            {notMonitored ? (
              <span className={classes.noData}>Not tracked yet</span>
            ) : syncing ? (
              <span className={classes.noData}>Syncing…</span>
            ) : platforms.length === 0 ? (
              <span className={classes.noData}>No Data</span>
            ) : (
              platforms.map((p) => (
                <div className={classes.platformItem} key={p.name}>
                  <span
                    className={classes.platformDot}
                    style={{ background: p.color }}
                  />
                  <span className={classes.platformName}>{p.name}</span>
                  <span className={classes.platformCount}>{p.count}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top Ad Headlines (from ad_creative_link_titles, not CTA buttons) */}
        <div className={classes.card}>
          <div className={classes.cardTop}>
            <div className={classes.cardTitleRow}>
              <CtaButtonClickIcon />
              <span className={classes.cardTitle}>Top Ad Headlines</span>
            </div>
          </div>
          <div className={classes.ctaList}>
            {ctaButtons.length === 0 ? (
              <span className={classes.noData}>No Data</span>
            ) : (
              ctaButtons.map((cta) => (
                <div className={classes.ctaItem} key={cta.label}>
                  <span className={classes.ctaDot} />
                  <span className={classes.ctaLabel} title={cta.label}>
                    {cta.label} -
                  </span>
                  <span className={classes.ctaCount}>{cta.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {isPolitical && (
        <div className={classes.grid}>
          {/* Impressions */}
          <div className={classes.card}>
            <div className={classes.cardTop}>
              <div className={classes.cardTitleRow}>
                <ImpressionsIcon />
                <span className={classes.cardTitle}>Impressions</span>
              </div>
            </div>
            <span className={classes.politicalValue}>1.2M</span>
          </div>

          {/* Audience Size */}
          <div className={classes.card}>
            <div className={classes.cardTop}>
              <div className={classes.cardTitleRow}>
                <AudienceSizeIcon />
                <span className={classes.cardTitle}>Audience Size</span>
              </div>
            </div>
            <span className={classes.politicalValue}>1.8M</span>
          </div>

          {/* Amount Spent */}
          <div className={classes.card}>
            <div className={classes.cardTop}>
              <div className={classes.cardTitleRow}>
                <AmountSpentIcon />
                <span className={classes.cardTitle}>Amount Spent</span>
              </div>
            </div>
            <span className={classes.politicalValue}>$24.5K</span>
          </div>

          {/* Disclaimer */}
          <div className={classes.card}>
            <div className={classes.cardTop}>
              <div className={classes.cardTitleRow}>
                <svg
                  fill="none"
                  height="24"
                  viewBox="0 0 24 24"
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    opacity="0.6"
                    r="10"
                    stroke="#25262D"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M12 8v4M12 16h.01"
                    opacity="0.6"
                    stroke="#25262D"
                    strokeLinecap="round"
                    strokeWidth="1.5"
                  />
                </svg>
                <span className={classes.cardTitle}>Disclaimer</span>
              </div>
            </div>
            <span className={classes.noData}>No Data</span>
          </div>
        </div>
      )}
    </>
  )
}
